import { useCallback, useState } from "react";
import {
    API,
    deleteAdministrationPatientDocument,
    getAdministrationPatientDocumentContent,
    uploadAdministrationPatientDocument,
} from "../../../services/api";
import { ADMIN_API_CONFIG } from "../model/administrationConstants";
import { buildAdministrationReportRows, openAdministrationReportPrint } from "../model/administrationReport";

// Propositions de modification envoyées par l'EHPAD, en attente de validation par la pharmacie.
export function useAdministrativeProposals({ ehpadId, disabled, onError, onReviewed }) {
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyIds, setBusyIds] = useState({});

    const load = useCallback(async () => {
        if (!ehpadId || disabled) {
            setProposals([]);
            return;
        }
        setLoading(true);
        onError("");
        try {
            const response = await API.get("/administration/propositions", {
                ...ADMIN_API_CONFIG,
                params: { status: "EN_ATTENTE", ehpadId },
            });
            setProposals(response.data);
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de charger les propositions EHPAD.");
        } finally {
            setLoading(false);
        }
    }, [disabled, ehpadId, onError]);

    const review = async (proposal, action) => {
        setBusyIds(current => ({ ...current, [proposal.id]: true }));
        onError("");
        try {
            await API.post(`/administration/propositions/${proposal.id}/${action}`, {}, ADMIN_API_CONFIG);
            setProposals(current => current.filter(item => item.id !== proposal.id));
            await onReviewed(proposal);
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de traiter cette proposition.");
        } finally {
            setBusyIds(current => ({ ...current, [proposal.id]: false }));
        }
    };

    return { proposals, loading, busyIds, load, review };
}

// Pièces jointes d'une fiche patient (autorisation de prélèvement, etc.).
export function usePatientDocuments({ replaceLoadedPatient, onError }) {
    const [busyIds, setBusyIds] = useState({});
    const run = async (patientId, action, failureMessage) => {
        setBusyIds(current => ({ ...current, [patientId]: true }));
        onError("");
        try {
            await action();
        } catch (requestError) {
            onError(requestError.response?.data?.message || failureMessage);
        } finally {
            setBusyIds(current => ({ ...current, [patientId]: false }));
        }
    };

    const upload = async (patientId, file) => {
        if (!patientId || !file) return;
        await run(patientId, async () => {
            replaceLoadedPatient((await uploadAdministrationPatientDocument(patientId, file)).data);
        }, "Impossible d'importer ce document.");
    };

    const open = async (patientId, document) => {
        if (!patientId || !document?.id) return;
        await run(patientId, async () => {
            const response = await getAdministrationPatientDocumentContent(patientId, document.id);
            const blob = new Blob([response.data], { type: document.contentType || response.data?.type || "application/octet-stream" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }, "Impossible d'ouvrir ce document.");
    };

    const remove = async (patientId, document) => {
        if (!patientId || !document?.id) return;
        if (!window.confirm(`Supprimer le document "${document.fileName || "Document"}" ?`)) return;
        await run(patientId, async () => {
            replaceLoadedPatient((await deleteAdministrationPatientDocument(patientId, document.id)).data);
        }, "Impossible de supprimer ce document.");
    };

    return { busyIds, upload, open, remove };
}

// Rapport administratif d'un EHPAD : préparé côté serveur, éditable avant impression en PDF.
export function useAdministrationReport({ ehpadId, ehpadName, onError }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const open = async () => {
        if (!ehpadId) return;
        setModalOpen(true);
        setLoading(true);
        onError("");
        try {
            const response = await API.get(`/administration/ehpads/${ehpadId}/report-candidates`, ADMIN_API_CONFIG);
            setRows(buildAdministrationReportRows(response.data));
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de préparer le rapport administratif.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    return {
        modalOpen,
        setModalOpen,
        rows,
        loading,
        open,
        updateRow: (key, patch) => setRows(current => current.map(row => row.key === key ? { ...row, ...patch } : row)),
        download: () => openAdministrationReportPrint({ ehpadName, rows }),
    };
}

// Import du classeur Excel (super admin) : contrôle à blanc puis application.
export function useAdministrationImport({ onError, onApplied }) {
    const [files, setFiles] = useState([]);
    const [mode, setMode] = useState("file");
    const [report, setReport] = useState(null);
    const [importing, setImporting] = useState(false);

    const importWorkbook = async dryRun => {
        if (files.length === 0) {
            onError("Sélectionnez d'abord un fichier ou un dossier de tableurs à importer.");
            return;
        }
        const form = new FormData();
        if (mode === "file") {
            form.append("file", files[0]);
        } else {
            files.forEach(file => form.append("files", file));
        }
        setImporting(true);
        onError("");
        try {
            const response = await API.post(`/administration/import?dryRun=${dryRun}`, form, {
                ...ADMIN_API_CONFIG,
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReport(response.data);
            if (response.data.applied) await onApplied();
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible d'importer ces fichiers Excel.");
        } finally {
            setImporting(false);
        }
    };

    return { files, setFiles, mode, setMode, report, setReport, importing, importWorkbook };
}
